import React, { useEffect } from 'react';
import { XIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './Card';
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md'
}: ModalProps) {
  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-155">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-156" />

      {/* Modal Content */}
      <div className={`relative z-50 w-full ${sizeClasses[size]} p-4 animate-in fade-in zoom-in-95 duration-200`} data-id="element-157">
        <Card className="shadow-2xl border-gray-200 bg-white w-full" data-id="element-158">
          <CardHeader className="flex flex-row items-start justify-between pb-4 border-b border-gray-100" data-id="element-159">
            <div className="space-y-1 pr-6" data-id="element-160">
              <CardTitle className="text-xl" data-id="element-161">{title}</CardTitle>
              {description && <CardDescription data-id="element-162">{description}</CardDescription>}
            </div>
            <button onClick={onClose} className="rounded-full p-1.5 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring" aria-label="Close modal" data-id="element-163">
              <XIcon className="h-4 w-4" data-id="element-164" />
            </button>
          </CardHeader>
          <CardContent className="pt-6" data-id="element-165">{children}</CardContent>
        </Card>
      </div>
    </div>;
}